import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { tap } from 'rxjs/operators';

import { AuthActions } from '@shared/store/auth.actions';
import { EntityState } from '@shared/store/entity.state';
import { StateModel } from '@shared/store/state.type';
import { StoreActions } from '@shared/store/store.actions';

import { Development } from '../models/development.model';
import {
  DevelopmentsApiService,
  DevelopmentsFilter,
} from '../services/developments-api.service';
import { DevelopmentsActions } from './developments.actions';

export type DevelopmentsStateModel = StateModel<Development> & {
  filters: DevelopmentsFilter | null;
};

export const DEVELOPMENTS_STATE_TOKEN =
    new StateToken<DevelopmentsStateModel>('DevelopmentsState');

const defaultState: DevelopmentsStateModel = {
  entities: {},
  selected: '',
  ids: [],
  isLoaded: false,
  hasError: false,
  filters: null,
};

@State<DevelopmentsStateModel>({
  name: DEVELOPMENTS_STATE_TOKEN,
  defaults: defaultState,
})
@Injectable()
export class DevelopmentsState extends EntityState {

  private _developmentsService = inject(DevelopmentsApiService);

  @Selector()
  static getAllFiltered(state: DevelopmentsStateModel): Development[] {
    const all = DevelopmentsState.getAll<Development>()(state);
    const filters = state.filters;

    if (!filters) {
      return all;
    }

    return all
        .filter((development) => {
          if (!filters.projectId) {
            return true;
          }
          return development.project.id === filters.projectId;
        })
        .filter((development) => {
          if (!filters.regionId) {
            return true;
          }
          return development.regionId === filters.regionId;
        });
  }

  @Selector()
  static getFilter(state: DevelopmentsStateModel): DevelopmentsFilter | null {
    return state.filters;
  }

  @Selector()
  static getAllFavorites(state: DevelopmentsStateModel): Development[] {
    return DevelopmentsState.getAllFiltered(state)
                            .filter((development) => development.isFavorite);
  }

  @Action(DevelopmentsActions.Get)
  getAll(
      ctx: StateContext<DevelopmentsStateModel>,
      action: DevelopmentsActions.Get,
  ) {

    ctx.patchState({
      isLoaded: false,
    });

    return this._developmentsService
               .get$(action.payload)
               .pipe(
                   tap((items) => {
                     if (!items) {
                       return;
                     }
                     const state = ctx.getState();
                     ctx.patchState({
                       ...EntityState.getStateWithNewValues(state, items),
                       ...EntityState.getApiLoadSuccessConfig(),
                       filters: action.payload ?? state.filters,
                     });
                   }),
               );
  }

  @Action(DevelopmentsActions.GetById)
  getById(
      ctx: StateContext<DevelopmentsStateModel>,
      action: DevelopmentsActions.GetById,
  ) {

    ctx.patchState({
      isLoaded: false,
    });

    return this._developmentsService
               .getById$(action.payload)
               .pipe(
                   tap((item) => {
                     if (!item) {
                       return;
                     }
                     ctx.patchState({
                       ...EntityState.getStateWithNewValues(
                           ctx.getState(),
                           [item],
                       ),
                       ...EntityState.getApiLoadSuccessConfig(),
                     });
                   }),
               );
  }

  @Action(DevelopmentsActions.ToggleFavorite)
  toggleFavorite(
      ctx: StateContext<DevelopmentsStateModel>,
      action: DevelopmentsActions.ToggleFavorite,
  ) {
    return this._developmentsService
               .toggleFavorite$(action.payload)
               .pipe(
                   tap(() => {
                     const state = ctx.getState();
                     const source = state.entities[String(action.payload)];

                     if (!source) {
                       return;
                     }

                     const favorite = Object.assign({}, source);
                     favorite.isFavorite = !favorite.isFavorite;

                     ctx.patchState({
                       ...EntityState.getUpdatedEntities(state, [favorite]),
                       ...EntityState.getApiLoadSuccessConfig(),
                     });
                   }),
               );
  }

  @Action([
    DevelopmentsActions.GetFailed,
    DevelopmentsActions.GetByIdFailed,
    DevelopmentsActions.ToggleFavoriteFailed,
  ])
  requestFailed(ctx: StateContext<DevelopmentsStateModel>) {
    ctx.patchState(EntityState.getApiLoadErrorConfig());
  }

  @Action(DevelopmentsActions.Select)
  select(
      ctx: StateContext<DevelopmentsStateModel>,
      action: DevelopmentsActions.Select,
  ) {
    ctx.patchState({
      selected: action.payload,
    });
  }

  @Action([
    StoreActions.ResetLoadedState,
    DevelopmentsActions.ResetLoadedState,
  ])
  resetLoadedState(ctx: StateContext<DevelopmentsStateModel>) {
    ctx.patchState({
      isLoaded: defaultState.isLoaded,
      hasError: defaultState.hasError,
    });
  }

  @Action([
    DevelopmentsActions.ResetState,
    StoreActions.ResetStore,
    AuthActions.Unauthorized,
  ])
  resetState(ctx: StateContext<DevelopmentsStateModel>) {
    ctx.setState({
      ...defaultState,
    });
  }
}
